/**
 * 视频版播放策略 - 视频播放
 * 包装 VideoController，提供视频播放功能
 */
import { PlayStrategy } from './PlayStrategy.js';
import VideoController, { ALIYUN_VIDEO_HLS_PLAYBACK_ENABLED } from '../VideoController.js';
import { songHasMusicTag } from '@/utils/songTags.js';

export class VideoStrategy extends PlayStrategy {
  constructor() {
    super();
    
    // 🔥 每次创建新策略时，创建新的 VideoController 实例
    this.videoController = new VideoController();
    this.currentSong = null;
    
    // 绑定基础播放器的事件到策略事件系统
    this.bindVideoControllerEvents();
    
    console.log('🎬 [VideoStrategy] 视频版策略创建完成');
  }
  
  /**
   * 绑定 VideoController 事件到策略事件系统
   */
  bindVideoControllerEvents() {
    console.log('🎬 [VideoStrategy] 开始绑定 VideoController 事件监听器');
    
    // 播放事件
    this.videoController.on('play', () => {
      this.emit('play', {});
    });
    
    // 🔥 新增：正在播放事件（视频真正开始播放时触发，用户能看到画面和听到声音）
    // 这是最准确的播放状态标志，用于更新全局播放状态
    this.videoController.on('playing', () => {
      console.log('🎬 [VideoStrategy] 🔥 收到 VideoController 的 playing 事件，转发到策略层');
      // 🔥 关键修复：确保 playing 事件正确转发到 PlaybackStrategyManager
      // PlaybackStrategyManager 会监听此事件并更新全局状态
      this.emit('playing', {});
    });
    
    console.log('🎬 [VideoStrategy] ✅ 事件监听器绑定完成（包括 playing 事件）');
    
    // 暂停事件
    this.videoController.on('pause', () => {
      this.emit('pause', {});
    });
    
    // 时间更新事件
    this.videoController.on('timeupdate', (data) => {
      this.emit('timeupdate', {
        currentTime: data.currentTime,
        duration: data.duration
      });
    });
    
    // 播放结束事件
    this.videoController.on('ended', () => {
      this.emit('ended', {});
    });
    
    // 错误事件
    this.videoController.on('error', (data) => {
      this.emit('error', { error: data.error });
    });
    
    // 元数据加载完成事件
    this.videoController.on('loadedmetadata', (data) => {
      this.emit('loadedmetadata', { duration: data.duration });
    });
    
    // 可以播放事件
    this.videoController.on('canplay', () => {
      this.emit('canplay', {});
    });
    
    // 视频URL更新事件（视频专用）
    this.videoController.on('videoUrlUpdated', (data) => {
      this.emit('videoUrlUpdated', data);
    });
  }
  
  /**
   * 加载歌曲
   * @param {Object} song - 歌曲对象
   * @returns {Promise<void>}
   */
  async load(song) {
    if (!song) {
      throw new Error('歌曲对象不能为空');
    }
    this.currentSong = song;
    
    console.log('🎬 [VideoStrategy] 加载视频歌曲:', song.title, {
      songId: song.id,
      videoId: song.videoId,
      videoDirectUrl: song.videoDirectUrl,
      isShareMusic: song.isShareMusic
    });
    
    // 🔥 修复：分享页面的视频版歌曲，传递 songId 和 videoDirectUrl
    // 在分享场景下，direct-url 请求返回了 musicId，不需要从URL中提取
    // VideoController 会直接使用 videoDirectUrl 而不需要提取ID
    const songId = song.id || song.videoId;
    
    if (!songId) {
      throw new Error(`视频歌曲缺少ID (song: ${JSON.stringify(song)})`);
    }
    
    if (ALIYUN_VIDEO_HLS_PLAYBACK_ENABLED && song.isShareMusic && song.videoHlsStreams?.length) {
      console.log('🎬 [VideoStrategy] 分享页面视频版歌曲，使用阿里云 HLS 流');
      await this.videoController.loadVideoSong(songId, { hlsStreams: song.videoHlsStreams });
      console.log('🎬 [VideoStrategy] 视频加载完成（使用 HLS 流）');
      return;
    }

    if (song.isShareMusic && song.videoDirectUrl) {
      console.log('🎬 [VideoStrategy] 分享页面视频版歌曲，传递 songId 和 videoDirectUrl:', {
        songId,
        videoDirectUrl: song.videoDirectUrl.substring(0, 50) + '...'
      });
      await this.videoController.loadVideoSong(songId, { videoUrl: song.videoDirectUrl });
      console.log('🎬 [VideoStrategy] 视频加载完成（使用直链）');
      return;
    }
    
    // 🔥 优化：直接使用 songId 传递给 VideoController
    // VideoController 现在支持直接接收 songId（数字或字符串），无需构造 URL
    // VideoController 会按照：缓存 > HLS（阿里云）> 原文件直链 > 流式接口 的顺序尝试加载
    console.log('🎬 [VideoStrategy] 直接传递歌曲ID给 VideoController:', songId);
    console.log(
      '🎬 [VideoStrategy] VideoController 将按优先级处理:',
      ALIYUN_VIDEO_HLS_PLAYBACK_ENABLED ? '缓存 > HLS > 原文件直链 > 流式' : '缓存 > 原文件直链(Range) > 流式'
    );

    const videoStorageType = song.videoStorageType != null ? song.videoStorageType : song.audioStorageType;
    await this.videoController.loadVideoSong(songId, {
      storageType: videoStorageType,
      videoId: song.videoId
    });
    
    console.log('🎬 [VideoStrategy] 视频加载完成');
  }
  
  /**
   * 播放
   * @returns {Promise<void>}
   */
  async play() {
    console.log('🎬 [VideoStrategy] 开始播放视频');
    
    // 使用 VideoController 的 play 方法（它会处理视频元素的获取和播放）
    // 如果 videoController.play() 返回 false，说明视频元素不存在，需要等待
    try {
      let videoElement = this.videoController.getVideoElement();
      
      // 如果视频元素不存在，等待 DOM 渲染
      if (!videoElement) {
        console.log('🎬 [VideoStrategy] 视频元素不存在，等待DOM渲染...');
        videoElement = await this.videoController.waitForVideoElement();
      }
      
      if (videoElement) {
        try {
          await videoElement.play();
          console.log('🎬 [VideoStrategy] 视频播放成功');
          // 🔥 移除直接更新 Store 的逻辑，避免循环更新
          // 播放状态应该由 VideoController 的 playing 事件 -> VideoStrategy 的 playing 事件 -> PlaybackStrategyManager 来更新
          // 这样确保状态更新的单一来源，避免循环
        } catch (error) {
          console.error('🎬 [VideoStrategy] 视频播放失败:', error);
          throw error;
        }
      } else {
        // 如果还是不存在，尝试使用 VideoController 的 play 方法
        const played = await this.videoController.play();
        if (!played) {
          throw new Error('视频元素不存在，无法播放视频');
        }
        console.log('🎬 [VideoStrategy] 视频播放成功');
        // 🔥 移除直接更新 Store 的逻辑，避免循环更新
      }
    } catch (error) {
      console.error('🎬 [VideoStrategy] 视频播放失败:', error);
      throw error;
    }
  }
  
  /**
   * 暂停
   */
  pause() {
    console.log('🎬 [VideoStrategy] 暂停播放视频');
    const videoElement = this.videoController.getVideoElement();
    if (videoElement) {
      videoElement.pause();
    }
  }
  
  /**
   * 停止
   */
  stop() {
    console.log('🎬 [VideoStrategy] 停止播放视频');
    const videoElement = this.videoController.getVideoElement();
    if (videoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
    }
  }
  
  /**
   * 设置播放时间
   * @param {number} time - 播放时间（秒）
   */
  setCurrentTime(time) {
    const videoElement = this.videoController.getVideoElement();
    if (videoElement) {
      videoElement.currentTime = time;
    }
  }
  
  /**
   * 获取当前播放时间
   * @returns {number}
   */
  getCurrentTime() {
    const videoElement = this.videoController.getVideoElement();
    return videoElement ? videoElement.currentTime : 0;
  }
  
  /**
   * 获取总时长
   * @returns {number}
   */
  getDuration() {
    const videoElement = this.videoController.getVideoElement();
    return videoElement ? videoElement.duration : 0;
  }
  
  /**
   * 是否正在播放
   * @returns {boolean}
   */
  isPlaying() {
    const videoElement = this.videoController.getVideoElement();
    return videoElement ? !videoElement.paused : false;
  }
  
  /**
   * 获取视频元素（供外部使用）
   * @returns {HTMLVideoElement|null}
   */
  getVideoElement() {
    // cleanup() 之后 PlaybackStrategyManager 还可能拿着这个实例调一次，
    // 直接取 videoController 会抛 "Cannot read properties of null"
    return this.videoController ? this.videoController.getVideoElement() : null;
  }
  
  /**
   * 🔥 新增：全屏相关方法
   */
  
  /**
   * 检查是否处于全屏状态
   * @returns {boolean}
   */
  isInFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.isInFullscreen) {
      // 检查视频全屏（isVideoFullscreen）或视频全屏模态（showVideoFullscreen）
      return this.fullscreenControls.isInFullscreen('video') || 
             this.fullscreenControls.isInFullscreen('videoModal');
    }
    return false;
  }
  
  /**
   * 进入全屏（如果需要的话）
   * 当切换歌曲时，如果之前处于全屏状态，会调用此方法
   * @returns {Promise<void>}
   */
  async enterFullscreenIfNeeded() {
    if (!this.fullscreenControls?.enterFullscreen) {
      return;
    }

    const playerPageOpen = this.fullscreenControls.isPlayerPageOpen?.() === true;
    const shouldAutoEnter = this.fullscreenControls.shouldAutoEnterFullscreen?.() === true;
    if (songHasMusicTag(this.currentSong) && !shouldAutoEnter) {
      console.log('🎬 [VideoStrategy] 带音乐标签，跳过自动进入全屏');
      return;
    }
    if (playerPageOpen && !shouldAutoEnter) {
      console.log('🎬 [VideoStrategy] 播放页已打开，跳过自动进入全屏');
      return;
    }

    console.log('🎬 [VideoStrategy] 进入视频全屏模式');
    await this.fullscreenControls.enterFullscreen('video');
  }
  
  /**
   * 退出全屏
   * @returns {Promise<void>}
   */
  async exitFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.exitFullscreen) {
      console.log('🎬 [VideoStrategy] 退出视频全屏模式');
      await this.fullscreenControls.exitFullscreen('video');
    }
  }
  
  /**
   * 清理资源
   */
  cleanup() {
    console.log('🧹 [VideoStrategy] 开始清理资源');
    
    // 清理 VideoController
    if (this.videoController) {
      // 停止视频播放
      const videoElement = this.videoController.getVideoElement();
      if (videoElement) {
        videoElement.pause();
        videoElement.src = '';
        videoElement.load();
      }
      
      // 清理 VideoController
      if (this.videoController.cleanup) {
        this.videoController.cleanup();
      }
      this.videoController = null;
    }
    
    // 调用父类清理方法（清理事件监听器）
    super.cleanup();
    
    console.log('✅ [VideoStrategy] 资源清理完成');
  }
}

